import { signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import {
  AlertCircle,
  CheckCircle2,
  Copy,
  KeyRound,
  Link2,
  Loader2,
  Save,
  ShieldCheck
} from "lucide-react";
import { Skeleton } from "@app/ui/components/skeleton";
import {
  api,
  ApiError,
  type GoogleAiConnectionConfig
} from "./api";
import { friendlyError } from "@app/ui/lib/friendly-error";

const data = signal<GoogleAiConnectionConfig | null>(null);
const phase = signal<"loading" | "ready" | "error">("loading");
const errMsg = signal<string | null>(null);
const saving = signal(false);
const savedAt = signal<number | null>(null);

async function load() {
  phase.value = "loading";
  errMsg.value = null;
  try {
    data.value = await api.getGoogleAiConnectionConfig();
    phase.value = "ready";
  } catch (err) {
    if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    errMsg.value = friendlyError(err);
    phase.value = "error";
  }
}

function sourceLabel(source: GoogleAiConnectionConfig["source"]) {
  if (source === "panel") return "Configurado no painel";
  if (source === "environment") return "Vindo do ambiente";
  if (source === "mixed") return "Painel + ambiente";
  return "Ainda nao configurado";
}

function sourceTone(source: GoogleAiConnectionConfig["source"]) {
  if (source === "panel") return "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300";
  if (source === "environment") return "border-sky-500/40 bg-sky-500/10 text-sky-700 dark:text-sky-300";
  if (source === "mixed") return "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300";
  return "border-border bg-background text-muted-foreground";
}

export function AiConnectionsConfigView() {
  useEffect(() => {
    load();
  }, []);
  const ph = phase.value;
  if (ph === "loading") return <AiConnectionsLoading />;
  if (ph === "error") return <AiConnectionsError msg={errMsg.value} />;
  return <AiConnectionsEditor initial={data.value!} />;
}

function AiConnectionsLoading() {
  return (
    <div class="space-y-3">
      <Skeleton className="h-16 rounded-md" />
      <Skeleton className="h-72 rounded-md" />
    </div>
  );
}

function AiConnectionsError({ msg }: { msg: string | null }) {
  return (
    <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3">
      <div class="flex items-start gap-2">
        <AlertCircle className="mt-0.5 size-5 shrink-0 text-destructive" />
        <div>
          <h3 class="text-sm font-bold">Nao consegui carregar a configuracao de conexao</h3>
          {msg && <p class="text-xs text-muted-foreground">{msg}</p>}
          <button
            type="button"
            onClick={() => load()}
            class="mt-2 rounded-md border border-border bg-background px-3 py-1 text-xs font-semibold hover:bg-accent"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    </div>
  );
}

function AiConnectionsEditor({ initial }: { initial: GoogleAiConnectionConfig }) {
  const [clientId, setClientId] = useState(initial.clientId);
  const [clientSecret, setClientSecret] = useState("");
  const [redirectUri, setRedirectUri] = useState(initial.redirectUri);
  const [projectId, setProjectId] = useState(initial.projectId);
  const [scopesText, setScopesText] = useState(initial.scopes.join("\n"));

  async function handleSave() {
    saving.value = true;
    try {
      const saved = await api.saveGoogleAiConnectionConfig({
        clientId,
        clientSecret: clientSecret.trim() || undefined,
        redirectUri,
        projectId,
        scopes: scopesText
      });
      data.value = saved;
      savedAt.value = saved.savedAt;
      setClientId(saved.clientId);
      setClientSecret("");
      setRedirectUri(saved.redirectUri);
      setProjectId(saved.projectId);
      setScopesText(saved.scopes.join("\n"));
    } catch (err) {
      alert(err instanceof Error ? `Falha ao salvar: ${err.message}` : "Falha ao salvar.");
    } finally {
      saving.value = false;
    }
  }

  async function copyText(value: string) {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      // ignora
    }
  }

  const current = data.value ?? initial;

  return (
    <div class="space-y-4">
      <div class="flex flex-wrap items-center gap-3 rounded-md border border-border bg-card/40 px-4 py-3">
        <span
          class={`inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm font-semibold ${current.enabled ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300" : "border-border bg-background text-muted-foreground"}`}
        >
          <span
            class={`size-2.5 rounded-full ${current.enabled ? "bg-emerald-500" : "bg-muted-foreground/40"}`}
          />
          {current.enabled ? "Conectar Google pronto" : "Configuracao incompleta"}
        </span>
        <span
          class={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-semibold ${sourceTone(current.source)}`}
        >
          <Link2 className="size-3.5" />
          {sourceLabel(current.source)}
        </span>
        <span class="hidden text-xs text-muted-foreground sm:inline">
          Depois de salvo, o aluno so escolhe Gemini e clica em Conectar Google.
        </span>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving.value}
          class="ml-auto inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-1.5 text-sm font-bold text-white shadow-sm transition-colors hover:brightness-110 disabled:opacity-60"
        >
          {saving.value ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Save width="1.1em" height="1.1em" />
          )}
          Salvar
        </button>
      </div>

      {savedAt.value && (
        <div class="inline-flex items-center gap-1.5 rounded-md bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-700 dark:text-emerald-300">
          <CheckCircle2 width="1.1em" height="1.1em" />
          Salvo agora ha pouco
        </div>
      )}

      <div class="grid gap-4 xl:grid-cols-[minmax(0,1.2fr)_360px]">
        <section class="space-y-4 rounded-md border border-border bg-card/40 p-4">
          <header class="space-y-1">
            <h3 class="flex items-center gap-2 text-base font-bold">
              <KeyRound className="size-4 text-primary" />
              Credenciais Google para Gemini
            </h3>
            <p class="text-sm text-muted-foreground">
              Esta configuracao e da plataforma. O aluno nao ve estes campos.
            </p>
          </header>

          <div class="grid gap-3 lg:grid-cols-2">
            <LabeledField
              label="ID do cliente Google"
              value={clientId}
              placeholder="Seu client_id do Google Cloud"
              onChange={setClientId}
            />
            <LabeledField
              label="Segredo do cliente Google"
              value={clientSecret}
              placeholder={
                current.clientSecretConfigured
                  ? "Ja configurado - preencha so se quiser trocar"
                  : "Cole o client_secret"
              }
              onChange={setClientSecret}
              type="password"
            />
            <div class="lg:col-span-2">
              <LabeledField
                label="URL de retorno"
                value={redirectUri}
                placeholder="https://seu-dominio/api/student/ai-google-callback"
                onChange={setRedirectUri}
              />
            </div>
            <LabeledField
              label="ID do projeto Google"
              value={projectId}
              placeholder="Opcional - usado como projeto de cobranca/permissoes"
              onChange={setProjectId}
            />
            <div class="lg:col-span-2">
              <LabeledArea
                label="Escopos OAuth"
                value={scopesText}
                placeholder="Um escopo por linha"
                onChange={setScopesText}
                rows={5}
              />
            </div>
          </div>
        </section>

        <aside class="space-y-4">
          <div class="rounded-md border border-border bg-card/40 p-4">
            <h3 class="flex items-center gap-2 text-sm font-bold">
              <ShieldCheck className="size-4 text-primary" />
              Como fica para o aluno
            </h3>
            <ol class="mt-3 list-decimal space-y-2 pl-4 text-sm text-muted-foreground">
              <li>Entrar no Assistente IA.</li>
              <li>Escolher Gemini.</li>
              <li>Clicar em Conectar Google.</li>
              <li>Autorizar e voltar para o portal conectado.</li>
            </ol>
          </div>

          <div class="rounded-md border border-border bg-card/40 p-4">
            <h3 class="text-sm font-bold">URLs de retorno prontas</h3>
            <div class="mt-3 space-y-2">
              {current.help.redirectExamples.map((item) => (
                <CopyRow key={item} value={item} onCopy={() => copyText(item)} />
              ))}
            </div>
            <p class="mt-3 text-xs text-muted-foreground">
              Cadastre uma destas URLs no Google Cloud Console, conforme o ambiente.
            </p>
          </div>

          <div class="rounded-md border border-border bg-card/40 p-4">
            <h3 class="text-sm font-bold">Ultima edicao</h3>
            <p class="mt-2 text-sm text-muted-foreground">
              {current.savedBy && current.savedAt
                ? `${current.savedBy} · ${new Date(current.savedAt).toLocaleString("pt-BR")}`
                : "Ainda nao houve salvamento pelo painel."}
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}

function CopyRow({ value, onCopy }: { value: string; onCopy: () => void }) {
  return (
    <div class="flex items-start gap-2 rounded-md border border-border bg-background/70 px-3 py-2">
      <code class="min-w-0 flex-1 break-all text-[11px] text-foreground/90">{value}</code>
      <button
        type="button"
        onClick={onCopy}
        class="rounded-md border border-border bg-background px-2 py-1 text-xs font-semibold hover:bg-accent"
      >
        <Copy className="size-3.5" />
      </button>
    </div>
  );
}

function LabeledField({
  label,
  value,
  placeholder,
  onChange,
  type = "text"
}: {
  label: string;
  value: string;
  placeholder?: string;
  onChange: (value: string) => void;
  type?: "text" | "password";
}) {
  return (
    <label class="block">
      <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onInput={(e) => onChange((e.target as HTMLInputElement).value)}
        class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
      />
    </label>
  );
}

function LabeledArea({
  label,
  value,
  placeholder,
  onChange,
  rows = 4
}: {
  label: string;
  value: string;
  placeholder?: string;
  onChange: (value: string) => void;
  rows?: number;
}) {
  return (
    <label class="block">
      <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <textarea
        value={value}
        placeholder={placeholder}
        rows={rows}
        onInput={(e) => onChange((e.target as HTMLTextAreaElement).value)}
        class="w-full resize-y rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
      />
    </label>
  );
}
